import { useCallback } from 'react';
import { useProgress } from '@/contexts/ProgressContext';
import type { QuickCheck, QuickCheckProgress } from '@/types';

interface UseQuickCheckResult {
  state: QuickCheckProgress['state'];
  selectedOption?: string;
  submit: (optionId: string) => 'correct' | 'wrong';
  reset: () => void;
}

/** Reactive Quick Check controller — replaces legacy `qcReveal` IIFE per A.2.1. */
export function useQuickCheck(ticketId: string, qc: QuickCheck | undefined): UseQuickCheckResult {
  const { getTicket, setQuickCheck } = useProgress();
  const tp = getTicket(ticketId);
  const qcState = tp?.quickCheck?.state ?? 'pristine';
  const selectedOption = tp?.quickCheck?.selectedOption;

  const submit = useCallback(
    (optionId: string): 'correct' | 'wrong' => {
      const correct = qc?.answer === optionId;
      const next: QuickCheckProgress = {
        state: correct ? 'correct' : 'wrong',
        selectedOption: optionId,
        revealedAt: new Date().toISOString(),
      };
      setQuickCheck(ticketId, next);
      return correct ? 'correct' : 'wrong';
    },
    [setQuickCheck, ticketId, qc?.answer],
  );

  const reset = useCallback(() => {
    setQuickCheck(ticketId, { state: 'pristine' });
  }, [setQuickCheck, ticketId]);

  return { state: qcState, selectedOption, submit, reset };
}
